import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { signinUrlFor } from "../utils/returnTo";

// Single source of truth for who the current user is and what they may do.
//
// ── The two-level model ──────────────────────────────────────────────────────
//
//   userType    the PORTAL: "admin" | "reseller" | "user". Decides which
//               dashboard and navigation the user gets.
//   role        the role slug within that portal ("admin", "sales",
//               "marketing", …). Informational here — never gate on it.
//   permissions the flat list of permission keys the role grants. THIS is what
//               tabs and buttons are gated on, via usePermission() or <Can>.
//
// Gate on permissions, not on role. `useIsRole("sales")` breaks the moment
// somebody creates a "marketing" role that should see the same screen;
// `usePermission("admin.marketing.emails")` keeps working.
//
// SECURITY: none of this is a security boundary. Everything here is a UI
// convenience so users aren't shown buttons that would 403. The server
// re-checks the permission on every single request regardless of what the
// client believes — see middleware/permissions.js in the backend. Never treat
// a hidden button as protection.
//
// localStorage is likewise never trusted for authorisation: it is editable from
// devtools, so a customer could flip "userType" to "admin" and see (but never
// successfully use) admin UI. The values below come from /auth/verifytoken,
// which derives them from the database on every call.
//
// AUTH ERROR HANDLING (matches backend middleware contract):
//   401 + code "token_expired" | "no_token"   → refresh + retry
//   401 + code "refresh_expired"              → force re-login
//   403 + code "forbidden"                    → surface to user, no redirect
//   (all other failures are passed through as-is)

// status values:
//   "loading" — verifytoken in flight, render a loader
//   "ready"   — verified; userType, role and permissions are trustworthy
//   "anon"    — no token, no session
const AuthContext = createContext({
  status: "anon",
  userType: null,
  role: null,
  roleName: null,
  email: null,
  permissions: [],
  verified: true,
  profileComplete: true,
  can: () => false,
  refresh: () => {},
  refreshPermissions: () => {},
  setSession: () => {},
  markVerified: () => {},
  setProfileComplete: () => {},
});

export const useAuth = () => useContext(AuthContext);

// Deprecated: gate on a permission instead. Kept so any straggling caller keeps
// compiling, but a role check cannot express "this new role should also see it".
export const useIsRole = (allowed) => {
  const { status, userType } = useAuth();
  if (status !== "ready") return false;
  return Array.isArray(allowed) ? allowed.includes(userType) : allowed === userType;
};

// Does the current user hold this permission?
//
//   const canEdit = usePermission("admin.users.update");
//
// Pass an array to mean "any of these".
export const usePermission = (key) => {
  const { can } = useAuth();
  return can(key);
};

// Declarative version of the same thing:
//
//   <Can permission="admin.users.create">
//     <button>Add user</button>
//   </Can>
//
// `fallback` renders instead when the permission is missing (default: nothing).
//
// CAVEAT — super roles hold EVERY key, including the reseller.* and user.* ones.
// So <Can permission="reseller.something"> is true for an Administrator too. When
// a control is an *alternative* for one portal rather than an addition (the
// reseller's view icon in place of an admin's edit icon, say), check the portal
// as well:  {userType === "reseller" && <Can permission="reseller.…">…</Can>}
export const Can = ({ permission, fallback = null, children }) => {
  const { can } = useAuth();
  return can(permission) ? <>{children}</> : <>{fallback}</>;
};

const EMPTY_SESSION = {
  status: "anon",
  userType: null,
  role: null,
  roleName: null,
  email: null,
  permissions: [],
  // Whether this account has confirmed its email address. NOT an authorisation
  // flag — an unverified user has full run of the portal and only sees a
  // reminder banner (see VerificationBanner). Signup deliberately does not wait
  // on it, because a verification wall is where signups get abandoned.
  //
  // Defaults to true rather than false: "anon" and "loading" have no account to
  // be unverified, and a false default would flash the prompt on every boot
  // before /auth/verifytoken answers.
  verified: true,
  // Same idea for signup's skippable second step: false means the user still
  // owes us a country, organization and phone number, and the dashboard's
  // setup card asks for them. Defaults true for the same reason as above.
  profileComplete: true,
};

export function AuthProvider({ children }) {
  const initial = localStorage.getItem("accessToken")
    ? { ...EMPTY_SESSION, status: "loading" }
    : { ...EMPTY_SESSION };

  const [state, setState] = useState(initial);

  // Single-flight refresh: if N requests fail with token_expired
  // simultaneously, they all await the same refresh attempt.
  const refreshPromiseRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_URL;

  // Stable handles used by both the interceptor and the public methods.
  const stateRef = useRef(state);
  stateRef.current = state;

  // The last x-permissions-version we saw on any response. When the server
  // reports a different one, some role's grants changed and our cached
  // permission list may be stale, so we re-fetch it. This is why no polling is
  // needed — every response the app already makes carries the signal.
  const permissionsVersionRef = useRef(null);
  const permissionsRefetchRef = useRef(null);

  const installSession = (u) => {
    localStorage.setItem("accessToken", u.accessToken);
    localStorage.setItem("refreshToken", u.refreshToken);
    localStorage.setItem("userType", u.userType);
    localStorage.setItem("email", u.email);
    setState({
      status: "ready",
      userType: u.userType,
      role: u.role || null,
      roleName: u.roleName || null,
      email: u.email,
      permissions: u.permissions || [],
      verified: u.verified !== false,
      profileComplete: u.profileComplete !== false,
    });
  };

  const clearAndLogout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userType");
    localStorage.removeItem("email");
    setState({ ...EMPTY_SESSION });
    // Bounce to /signin if the user is currently sitting on a guarded page.
    // Skip the redirect when they're already on a public/auth page.
    const path = window.location.pathname.toLowerCase();
    const publicPaths = ["/signin", "/signup", "/forgotpasswordrequest", "/resetpassword", "/postpayment"];
    const onPublicPage = publicPaths.some(p => path === p || path.startsWith(p + "/"));
    if (!onPublicPage) {
      // A full reload, so the destination has to ride in the URL: this is the
      // path a customer with a stale session takes when they click a deep link
      // (e.g. a renewal email) — without `next` they'd land on the dashboard.
      window.location.href = signinUrlFor(window.location.pathname + window.location.search);
    }
  };

  // Attempts to refresh the access token. Returns the new accessToken on
  // success, null on failure (and triggers logout).
  const performRefresh = async () => {
    const refreshTok = localStorage.getItem("refreshToken");
    if (!refreshTok) {
      clearAndLogout();
      return null;
    }
    try {
      const res = await axios.get(`${apiUrl}/auth/gettokens`, {
        headers: { refresh_token: refreshTok },
        // Mark so the interceptor leaves this alone.
        _skipAuthRefresh: true,
      });
      if (res.data?.status === 1 && res.data.user?.accessToken) {
        installSession(res.data.user);
        return res.data.user.accessToken;
      }
      clearAndLogout();
      return null;
    } catch (err) {
      // 401 here = refresh token also gone. Tell the user once, log them out.
      const code = err.response?.data?.code;
      if (code === "refresh_expired" || code === "no_refresh_token") {
        toast.error("Session expired. Please sign in again.");
      } else {
        console.error("Token refresh failed:", err);
      }
      clearAndLogout();
      return null;
    }
  };

  // Re-reads the caller's identity and permission list from the server. Called
  // on boot, and again whenever x-permissions-version moves.
  const fetchSession = async ({ silent = false } = {}) => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      setState({ ...EMPTY_SESSION });
      return null;
    }
    const response = await axios.get(`${apiUrl}/auth/verifytoken`, {
      headers: { "access-token": token },
    });
    if (response.data?.status === 1 && response.data.user?.userType) {
      const u = response.data.user;
      localStorage.setItem("userType", u.userType);
      if (u.email) localStorage.setItem("email", u.email);
      setState({
        status: "ready",
        userType: u.userType,
        role: u.role || null,
        roleName: u.roleName || null,
        email: u.email,
        permissions: u.permissions || [],
        verified: u.verified !== false,
        profileComplete: u.profileComplete !== false,
      });
      return u;
    }
    if (!silent) clearAndLogout();
    return null;
  };

  // Single-flight: a burst of responses carrying a new version triggers one
  // re-fetch, not one per response.
  const refreshPermissions = () => {
    if (permissionsRefetchRef.current) return permissionsRefetchRef.current;
    permissionsRefetchRef.current = fetchSession({ silent: true })
      .catch(() => null)
      .finally(() => { permissionsRefetchRef.current = null; });
    return permissionsRefetchRef.current;
  };

  // Compares the version header on any response against the last one seen.
  const notePermissionsVersion = (response) => {
    const version = response?.headers?.["x-permissions-version"];
    if (!version) return;

    const previous = permissionsVersionRef.current;
    permissionsVersionRef.current = version;

    // First sighting just records the baseline — nothing has changed yet.
    if (previous === null || previous === version) return;
    // Only meaningful once we have a session to update.
    if (stateRef.current.status !== "ready") return;

    refreshPermissions();
  };

  // Install the axios response interceptor BEFORE the first verifytoken call
  // so that call gets the same refresh/retry behaviour as the rest of the
  // app. Registered once on mount, ejected on unmount.
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => {
        notePermissionsVersion(response);
        return response;
      },
      async (error) => {
        const original = error.config;
        const status = error.response?.status;
        const code = error.response?.data?.code;

        // No response (network error) or already-retried request → pass through.
        if (!original || !error.response) return Promise.reject(error);

        // A 403 is itself a strong hint that our cached permissions are stale,
        // so check the version header on failures too.
        notePermissionsVersion(error.response);

        if (original._retried || original._skipAuthRefresh) return Promise.reject(error);

        // Don't try to refresh the refresh endpoint itself, or the signin/signup endpoints.
        const url = original.url || "";
        if (url.includes("/auth/gettokens") || url.includes("/auth/signin")) {
          return Promise.reject(error);
        }

        // 403 = authenticated but not allowed. Don't refresh — caller should
        // surface "You're not authorized for this action" to the user.
        if (status === 403) {
          return Promise.reject(error);
        }

        // 401 with refresh-relevant code = try to recover by refreshing.
        if (status === 401 && (code === "token_expired" || code === "no_token")) {
          original._retried = true;

          if (!refreshPromiseRef.current) {
            refreshPromiseRef.current = performRefresh().finally(() => {
              refreshPromiseRef.current = null;
            });
          }

          const newAccessToken = await refreshPromiseRef.current;
          if (!newAccessToken) {
            // performRefresh already toasted "Session expired" and started a
            // navigation to /signin. Returning a never-settling promise
            // prevents the original caller's .catch from running its own
            // toast (e.g. "Access token expired.") in the brief window
            // before the page actually navigates away.
            return new Promise(() => {});
          }

          // Replay the original request with the fresh token. There is one
          // header convention now — `access-token`, what every backend route's
          // authenticate_access_token reads — so this sets it unconditionally
          // rather than trying to detect which header the caller used.
          original.headers = original.headers || {};
          original.headers["access-token"] = newAccessToken;
          return axios(original);
        }

        return Promise.reject(error);
      }
    );

    // Boot: verify the session we already have on disk (if any).
    const verify = async () => {
      try {
        await fetchSession();
      } catch (err) {
        // 401s reach here only if the interceptor's refresh also failed,
        // and the interceptor has already called clearAndLogout.
        if (err.response?.status !== 401) {
          console.error("verifytoken failed:", err);
          clearAndLogout();
        }
      }
    };

    verify();

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Public: synchronous session install — called right after /auth/signin
  // returns so guards see "ready" before navigation.
  const setSession = ({ userType, email, role, roleName, permissions, verified, profileComplete }) => {
    if (!userType) return;
    setState({
      status: "ready",
      userType,
      role: role || null,
      roleName: roleName || null,
      email: email || null,
      permissions: permissions || [],
      // Only an explicit false marks the account unverified — a caller that
      // doesn't know (or a server that doesn't say) must not trip the prompt.
      verified: verified !== false,
      profileComplete: profileComplete !== false,
    });
  };

  // Public: flip the current session to verified after /auth/verifyemail
  // succeeds, so the banner disappears without a round trip. The server is
  // still the authority — the next verifytoken confirms it.
  const markVerified = () => {
    setState(prev => (prev.status === "ready" ? { ...prev, verified: true } : prev));
  };

  // Public: reflect a profile save without waiting for the next verifytoken, so
  // the dashboard's setup card updates the moment the form succeeds. The server
  // recomputes the flag from what it actually stored and sends it back — pass
  // that through rather than assuming the save completed the profile.
  const setProfileComplete = (complete) => {
    setState(prev => (prev.status === "ready" ? { ...prev, profileComplete: complete !== false } : prev));
  };

  // Public: force a re-verification (used by logout to flip back to "anon").
  const refresh = async () => {
    try {
      await fetchSession();
    } catch {
      // The interceptor handles refresh; if we land here the user is logged out.
    }
  };

  // O(1) lookups, rebuilt only when the permission list actually changes.
  const permissionSet = useMemo(() => new Set(state.permissions || []), [state.permissions]);

  const can = useMemo(() => (key) => {
    if (state.status !== "ready") return false;
    if (!key) return true;                       // no permission required
    if (Array.isArray(key)) return key.some(k => permissionSet.has(k));
    return permissionSet.has(key);
  }, [permissionSet, state.status]);

  const value = useMemo(
    () => ({ ...state, can, refresh, refreshPermissions, setSession, markVerified, setProfileComplete }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, can]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
